export type ClinicianEditorDirtySnapshot =
  | {
      bodyText: string;
      defaultBodyText: string;
      defaultTitle: string;
      defaultVisibility?: "patient-visible" | "clinical";
      kind: "note";
      title: string;
      visibility: "patient-visible" | "clinical";
    }
  | {
      defaultDetailText: string;
      defaultTitle: string;
      detailText: string;
      kind: "record";
      title: string;
    };

export function isClinicianEditorDirty(
  snapshot: ClinicianEditorDirtySnapshot | null,
): boolean {
  if (snapshot == null) {
    return false;
  }

  if (snapshot.title !== snapshot.defaultTitle) {
    return true;
  }

  if (snapshot.kind === "record") {
    return snapshot.detailText !== snapshot.defaultDetailText;
  }

  return (
    snapshot.bodyText !== snapshot.defaultBodyText ||
    snapshot.visibility !==
      (snapshot.defaultVisibility ?? "patient-visible")
  );
}

export function hasUnsavedQuestionnaireReviewNote(
  value: string,
  baseline = "",
): boolean {
  return value.trim() !== baseline.trim();
}

export type ClinicianInlineEditorDirtySnapshot =
  | {
      bodyText: string;
      defaultBodyText: string;
      defaultTitle: string;
      defaultVisibility?: "patient-visible" | "clinical";
      kind: "note";
      title: string;
      visibility: "patient-visible" | "clinical";
    }
  | {
      defaultDetailText: string;
      defaultTitle: string;
      detailText: string;
      kind: "record";
      title: string;
    };

export function isClinicianInlineEditorDirty(
  snapshot: ClinicianInlineEditorDirtySnapshot | null,
): boolean {
  return isClinicianEditorDirty(snapshot);
}

export function hasClinicianUnsavedWorkspaceChanges(input: {
  clinicalEditorDirty: boolean;
  composerDraft: string;
  inlineEditorDirty: boolean;
  messageReviewEditorDirty: boolean;
  pendingMessageReviewEditCount: number;
  profileDirty: boolean;
  questionnaireReviewNote: string;
  questionnaireReviewNoteBaseline?: string;
}): boolean {
  return (
    input.profileDirty ||
    input.clinicalEditorDirty ||
    input.inlineEditorDirty ||
    input.messageReviewEditorDirty ||
    input.pendingMessageReviewEditCount > 0 ||
    input.composerDraft.trim().length > 0 ||
    hasUnsavedQuestionnaireReviewNote(
      input.questionnaireReviewNote,
      input.questionnaireReviewNoteBaseline,
    )
  );
}

export function shouldLoadClinicianQuestionnaireDefinition(
  loadedRequestKey: string | null,
  requestKey: string,
): boolean {
  return loadedRequestKey !== requestKey;
}

export type OwnedCallConfirmation = {
  patientId: string;
  targetId: string;
};

export function isMatchingOwnedCallConfirmation(
  current: OwnedCallConfirmation | null,
  completed: OwnedCallConfirmation,
): boolean {
  return (
    current != null &&
    current.patientId === completed.patientId &&
    current.targetId === completed.targetId
  );
}

/** Server-side conflicts that mean the confirmation modal is holding a stale
 *  snapshot. Retrying with the same snapshot fails identically forever, so the
 *  caller must reload the panel instead of leaving the button armed. */
const staleOutboundCallStateErrorCodes = [
  "outbound-call-state-changed",
  "outbound-questionnaire-session-not-resumable",
  "outbound-attempt-not-queued",
  "outbound-session-stale",
  "outbound-session-revision-required",
];

export function isStaleOutboundCallStateError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const messageSeparatorIndex = error.message.indexOf(": ");
  const codeSegment =
    messageSeparatorIndex < 0
      ? error.message
      : error.message.slice(0, messageSeparatorIndex);

  return staleOutboundCallStateErrorCodes.some((code) =>
    codeSegment.trim().endsWith(code),
  );
}

export function resolveClinicianOutboundCallErrorText(
  error: unknown,
): string {
  const fallbackMessage =
    "The questionnaire call could not be started. Review campaign readiness and try again.";

  if (!(error instanceof Error)) {
    return fallbackMessage;
  }

  const messageSeparatorIndex = error.message.indexOf(": ");

  if (messageSeparatorIndex < 0) {
    return fallbackMessage;
  }

  return (
    error.message.slice(messageSeparatorIndex + 2).trim() || fallbackMessage
  );
}

export type MedicationResolutionOwner = {
  entryIndex: number;
  patientId: string;
  questionKey: string;
  sessionId: string | null;
  targetId: string;
};

export function isMatchingMedicationResolutionOwner(
  current: MedicationResolutionOwner | null,
  expected: MedicationResolutionOwner,
): boolean {
  return (
    current != null &&
    current.entryIndex === expected.entryIndex &&
    current.patientId === expected.patientId &&
    current.questionKey === expected.questionKey &&
    current.sessionId === expected.sessionId &&
    current.targetId === expected.targetId
  );
}

export type OutboundCallOwner = {
  patientId: string;
  sessionId: string | null;
  targetId: string;
};

export function isMatchingOutboundCallOwner(
  current: OutboundCallOwner | null,
  expected: OutboundCallOwner,
): boolean {
  return (
    current != null &&
    current.patientId === expected.patientId &&
    current.sessionId === expected.sessionId &&
    current.targetId === expected.targetId
  );
}

export function isMatchingOutboundResponsePatient(
  requestedPatientId: string,
  responsePatientId: string,
): boolean {
  return requestedPatientId === responsePatientId;
}
